import React, { type FC, type PropsWithChildren } from 'react';

import { useTheme } from '@emotion/react';
import { getColor } from '@lucid/theme';
import type * as CSS from 'csstype';

import {
  ArrowRight,
  ArrowRightThin,
  ArrowRightLong,
  CarotDown,
  CarotRight,
  Checkmark,
  Ellipsis,
  EndQuote,
  Globe,
  Facebook,
  Glassdoor,
  Info,
  Linkedin,
  MagnifyingGlass,
  Pause,
  Play,
  PlayOutline,
  QuoteBubble,
  RSS,
  StarHalf,
  StarOutline,
  Star,
  StartQuote,
  Tag,
  Twitter,
  Warning,
  X,
  YouTube,
} from './icons';

export type IconTypes =
  | 'ArrowRight'
  | 'ArrowRightThin'
  | 'ArrowRightLong'
  | 'CarotRight'
  | 'CarotDown'
  | 'Checkmark'
  | 'Ellipsis'
  | 'EndQuote'
  | 'Facebook'
  | 'Glassdoor'
  | 'Globe'
  | 'Info'
  | 'Linkedin'
  | 'MagnifyingGlass'
  | 'Pause'
  | 'Play'
  | 'PlayOutline'
  | 'QuoteBubble'
  | 'RSS'
  | 'StarHalf'
  | 'StarOutline'
  | 'Star'
  | 'StartQuote'
  | 'Tag'
  | 'Twitter'
  | 'Warning'
  | 'X'
  | 'YouTube';

export const iconTypes = [
  'ArrowRight',
  'ArrowRightThin',
  'ArrowRightLong',
  'CarotRight',
  'CarotDown',
  'Checkmark',
  'Ellipsis',
  'EndQuote',
  'Facebook',
  'Glassdoor',
  'Globe',
  'Info',
  'Linkedin',
  'MagnifyingGlass',
  'Pause',
  'Play',
  'PlayOutline',
  'QuoteBubble',
  'RSS',
  'StarHalf',
  'StarOutline',
  'Star',
  'StartQuote',
  'Tag',
  'Twitter',
  'Warning',
  'X',
  'YouTube',
];

export const iconSize = ['auto', 'responsive'];

export type IconSizesType = 'auto' | 'responsive';

export interface IconBaseProps {
  /** The available icons to be rendered. */
  type: IconTypes;
  /** Sets the color of the icon. */
  color?: string | CSS.Property.Color;
}

export interface IconProps extends IconBaseProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the color when hovered for the icon. */
  hoverColor?: string;
  /** Sets how the icon should size when rendered. */
  sizing?: IconSizesType;
}

const IconComponent: FC<PropsWithChildren<IconBaseProps>> = ({ type, color }) => {
  switch (type) {
    case 'ArrowRight':
      return <ArrowRight color={color} />;

    case 'ArrowRightThin':
      return <ArrowRightThin color={color} />;

    case 'ArrowRightLong':
      return <ArrowRightLong color={color} />;

    case 'CarotRight':
      return <CarotRight color={color} />;

    case 'CarotDown':
      return <CarotDown color={color} />;

    case 'Checkmark':
      return <Checkmark color={color} />;

    case 'Ellipsis':
      return <Ellipsis color={color} />;

    case 'EndQuote':
      return <EndQuote color={color} />;

    case 'Globe':
      return <Globe color={color} />;

    case 'Facebook':
      return <Facebook color={color} />;

    case 'Glassdoor':
      return <Glassdoor color={color} />;

    case 'Info':
      return <Info color={color} />;

    case 'Linkedin':
      return <Linkedin color={color} />;

    case 'MagnifyingGlass':
      return <MagnifyingGlass color={color} />;

    case 'Pause':
      return <Pause color={color} />;

    case 'Play':
      return <Play color={color} />;

    case 'PlayOutline':
      return <PlayOutline color={color} />;

    case 'QuoteBubble':
      return <QuoteBubble color={color} />;

    case 'RSS':
      return <RSS color={color} />;

    case 'StarHalf':
      return <StarHalf color={color} />;

    case 'StarOutline':
      return <StarOutline color={color} />;

    case 'Star':
      return <Star color={color} />;

    case 'StartQuote':
      return <StartQuote color={color} />;

    case 'Tag':
      return <Tag color={color} />;

    case 'Twitter':
      return <Twitter color={color} />;

    case 'Warning':
      return <Warning color={color} />;

    case 'X':
      return <X color={color} />;

    case 'YouTube':
      return <YouTube color={color} />;
  }
};

const Icon: FC<PropsWithChildren<IconProps>> = ({ className, type, color, hoverColor, sizing = 'auto', ...rest }) => {
  const isResponsiveSizing = sizing === 'responsive';
  const theme = useTheme();

  let themeColor = getColor({
    color,
    theme,
  });
  let themeHoverColor = getColor({
    color: hoverColor,
    theme,
  });

  if (color === 'primary') {
    themeColor = theme.colors.primary;
  }

  if (hoverColor === 'primary') {
    themeHoverColor = theme.colors.primary;
  }

  if (color === 'secondary') {
    themeColor = theme.colors.secondary;
  }

  if (hoverColor === 'secondary') {
    themeHoverColor = theme.colors.secondary;
  }

  return (
    <span
      {...rest}
      css={[
        {
          label: `svgIcon-${type}`,
          display: 'inline-block',
          height: isResponsiveSizing ? '100%' : '1em',
          width: isResponsiveSizing ? '100%' : '1em',
          position: 'relative',
        },
        isResponsiveSizing && {
          verticalAlign: '-0.125em',
        },
        {
          'path:not([fill="none"])': {
            fill: themeColor || undefined,
            '&:hover': {
              fill: hoverColor ? themeHoverColor : undefined,
            },
          },
        },
      ]}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      data-testid={type}
    >
      {IconComponent({ type, color: themeColor })}
    </span>
  );
};

export { Icon };
export default Icon;
